import React, { useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { api } from '../services/api';
import { colors } from '../styles';
import Logger from '../utils/logger';

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '50px',
  padding: '8px 24px',
  backgroundColor: colors.primary,
  color: colors.white,
  '&:hover': {
    backgroundColor: colors.primary,
    opacity: 0.9
  },
  '&:disabled': {
    backgroundColor: `${colors.primary}50`,
    color: colors.white
  }
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: colors.status.error,
  marginTop: theme.spacing(1),
  textAlign: 'center',
}));

const PlaidLink = ({ onSuccess, onExit, buttonProps = {}, children }) => {
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getToken = async () => {
      try {
        setError(null);
        const response = await api.post('/plaid/create_link_token');
        if (response.data.link_token) {
          setToken(response.data.link_token);
        } else {
          throw new Error('No link token in response');
        }
      } catch (err) {
        console.error('Error creating link token:', err);
        setError('Failed to initialize Plaid. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    getToken();
  }, []);

  const { open, ready } = usePlaidLink({
    token,
    onSuccess: (public_token, metadata) => {
      const linkSessionId = metadata?.link_session_id;
      
      Logger.debug('[Plaid] Link success', {
        link_session_id: linkSessionId,
        institution_id: metadata?.institution?.institution_id,
        account_ids: metadata?.accounts?.map(a => a.id)
      });
      
      setLoading(true);
      setError(null);
      api.post('/plaid/exchange_public_token', {
        public_token,
        institution: metadata.institution,
        accounts: metadata.accounts,
        link_session_id: linkSessionId
      })
        .then(() => {
          onSuccess();
        })
        .catch((err) => {
          Logger.error('[Plaid] Exchange error:', err, {
            link_session_id: linkSessionId,
            error: err?.response?.data || err?.message
          });
          const apiErr = err?.response?.data;
          if (apiErr?.code === 'INSTITUTION_LIMIT_REACHED') {
            setError(
              apiErr?.error ||
                'You\'ve reached the free tier limit of 2 institutions. Upgrade for unlimited access.'
            );
          } else if (
            err?.response?.status === 409 ||
            apiErr?.error_type === 'DUPLICATE_ITEM' ||
            apiErr?.error_type === 'DUPLICATE_ACCOUNTS'
          ) {
            setError(
              apiErr?.display_message ||
                'These accounts are already connected. If you need to modify access, use update mode from your existing connection.'
            );
          } else {
            setError(apiErr?.display_message || 'Failed to connect account. Please try again.');
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    onExit: (err, metadata) => {
      Logger.debug('[Plaid] Link exit', {
        link_session_id: metadata?.link_session_id,
        error_type: err?.error_type,
        error_code: err?.error_code,
        status: metadata?.status
      });
      
      if (err) {
        Logger.error('[Plaid] Link error:', err);
        setError('Connection failed. Please try again.');
      }
      onExit?.(err);
    },
    onEvent: (eventName, metadata) => {
      Logger.debug('[Plaid] Link event', {
        event_name: eventName,
        link_session_id: metadata?.link_session_id,
        view_name: metadata?.view_name,
        institution_id: metadata?.institution_id,
        error_type: metadata?.error_type,
        error_code: metadata?.error_code
      });
    }
  });

  return (
    <Box>
      <StyledButton
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => {
          setError(null);
          open();
        }}
        disabled={!ready || loading}
        {...buttonProps}
      >
        {children || 'Add Account'}
      </StyledButton>
      {loading && !token && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Box>
  );
};

export default PlaidLink;